import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import api from "app/integration/api";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { Fragment, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";
import { angka } from "app/common/reportNumber";

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Penjualan per Produk", href: "#" },
];

function StoreReportSalesByProductPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [queryParams, setQueryParams] = useState({
    warehouse_id: null,
    ...defaultReportDates(),
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [rows, , loader] = useFetcher({
    modelName: "storeReport",
    methodName: "salesByProduct",
    payload: { id: queryParams },
  });

  // Total per item (produk) untuk baris grup dan kolom "Qty Item".
  const productTotals = (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    const current = acc[row.product_id] ?? { qty: 0, sub: 0, disc: 0, net: 0 };
    acc[row.product_id] = {
      qty: current.qty + Number(row.qty_sold ?? 0),
      sub: current.sub + angka(row.sub_total),
      disc: current.disc + angka(row.discount),
      net: current.net + Number(row.net_sales ?? 0),
    };
    return acc;
  }, {});

  const exportExcel = () => {
    const dataForExcel = (Array.isArray(rows) ? rows : []).map((row) => ({
      "Kode Produk": row.product_code,
      Produk: row.product_name,
      Varian: row.variant_name ?? "-",
      Size: row.size_name ?? "-",
      Warna: row.color_name ?? "-",
      "Qty Terjual": row.qty_sold,
      "Qty Item (Total)": productTotals[row.product_id]?.qty ?? row.qty_sold,
      Subtotal: angka(row.sub_total),
      Diskon: angka(row.discount),
      "Net Sales": row.net_sales,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: [
        "Kode Produk",
        "Produk",
        "Varian",
        "Size",
        "Warna",
        "Qty Terjual",
        "Qty Item (Total)",
        "Subtotal",
        "Diskon",
        "Net Sales",
      ],
    });
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 30 },
      { wch: 18 },
      { wch: 10 },
      { wch: 14 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Penjualan per Produk");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const formattedDate = new Date().toISOString().split("T")[0];
    link.download = `laporan_penjualan_per_produk_${formattedDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="sales-by-product"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Penjualan per Produk"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="store-report-sales-by-product-date-range"
                label="Tanggal"
                from={queryParams.start_date}
                to={queryParams.end_date}
                onFromChange={(e) =>
                  setQueryParams({
                    ...queryParams,
                    start_date: e.target.value,
                  })
                }
                onToChange={(e) =>
                  setQueryParams({ ...queryParams, end_date: e.target.value })
                }
              />
            </div>
            <Select testId="store-report-sales-by-product-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={queryParams.warehouse_id}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  warehouse_id: e.target.value,
                })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode Produk</Table.Heading>
              <Table.Heading>Varian</Table.Heading>
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              <Table.Heading>
                <p className="text-right">Qty Terjual</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Subtotal</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Diskon</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Net Sales</p>
              </Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {Array.isArray(rows) &&
              rows.map((row, index) => (
                <Fragment key={index}>
                  {/* Baris grup per item: muncul saat produk berganti (backend
                      sudah mengurutkan varian berkelompok per produk). */}
                  {rows[index - 1]?.product_id !== row.product_id && (
                    <Table.BodyRow>
                      <Table.Cell colSpan={4} fontWeight="semibold">
                        {row.product_code} — {row.product_name}
                      </Table.Cell>
                      <Table.Cell fontWeight="semibold">
                        <p className="text-right">
                          {productTotals[row.product_id]?.qty ?? 0}
                        </p>
                      </Table.Cell>
                      <Table.Cell fontWeight="semibold">
                        <p className="text-right">
                          <Typography.Currency
                            number={productTotals[row.product_id]?.sub ?? 0}
                          />
                        </p>
                      </Table.Cell>
                      <Table.Cell fontWeight="semibold">
                        <p className="text-right">
                          <Typography.Currency
                            number={productTotals[row.product_id]?.disc ?? 0}
                          />
                        </p>
                      </Table.Cell>
                      <Table.Cell fontWeight="semibold">
                        <p className="text-right">
                          <Typography.Currency
                            number={productTotals[row.product_id]?.net ?? 0}
                          />
                        </p>
                      </Table.Cell>
                    </Table.BodyRow>
                  )}
                  <Table.BodyRow>
                    <Table.Cell>{row.product_code}</Table.Cell>
                    <Table.Cell>{row.variant_name ?? "-"}</Table.Cell>
                    <Table.Cell>{row.size_name ?? "-"}</Table.Cell>
                    <Table.Cell>{row.color_name ?? "-"}</Table.Cell>
                    <Table.Cell>
                      <p className="text-right">{row.qty_sold}</p>
                    </Table.Cell>
                    <Table.Cell>
                      <p className="text-right">
                        <Typography.Currency number={angka(row.sub_total)} />
                      </p>
                    </Table.Cell>
                    <Table.Cell>
                      <p className="text-right">
                        <Typography.Currency number={angka(row.discount)} />
                      </p>
                    </Table.Cell>
                    <Table.Cell fontWeight="semibold">
                      <p className="text-right">
                        <Typography.Currency number={row.net_sales} />
                      </p>
                    </Table.Cell>
                  </Table.BodyRow>
                </Fragment>
              ))}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
      {Array.isArray(rows) && rows.length === 0 && !loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportSalesByProductPage;
